import { useCallback, useEffect, useState } from "react";
import { IBudget } from "../../../../models/budgetV2";
import { fetchBudgetsByOccurrenceV2, fetchBudgetUserProfile, fetchBudgetVersionsV2 } from "../../../../api/fetchBudgetV2";
import { BudgetRecipient } from "../../enums/budgetRecipient";

export type BudgetOccurrenceViewMode = "list" | "create" | "edit" | "edit-manager" | "details";

const FINISHED_STATUSES = ["Aprovado pelo gestor", "Recusado pelo gestor", "Rejeitado pelo gestor"];

const isRecipientUser = (recipient: string | undefined, persona: string): boolean => {
  switch (persona) {
    case "fleet-manager":
      return recipient === "MANAGER";
    case "dealership":
      return recipient === "CONSULTANT";
    case "control-tower":
      return recipient === "FACTORY";
    default:
      return false;
  }
};

export const useBudgetOccurrenceView = (occurrenceId: string, persona: string) => {
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState<IBudget[]>([]);
  const [profile, setProfile] = useState<BudgetRecipient | undefined>();
  const [view, setView] = useState<BudgetOccurrenceViewMode>("list");
  const [currentBudgetId, setCurrentBudgetId] = useState<string | undefined>();
  const [currentVersion, setCurrentVersion] = useState<number | undefined>();

  const loadBudgets = useCallback(async () => {
    if (!occurrenceId) return;
    setLoading(true);
    try {
      const response = await fetchBudgetsByOccurrenceV2(occurrenceId);
      setBudgets(response.content);
    } finally {
      setLoading(false);
    }
  }, [occurrenceId]);

  useEffect(() => {
    const loadProfile = async () => {
      const profileFetched = await fetchBudgetUserProfile();
      if (profileFetched) setProfile(profileFetched);
    };
    loadProfile();
  }, []);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  const goToList = useCallback(() => {
    setView("list");
    setCurrentBudgetId(undefined);
    setCurrentVersion(undefined);
    loadBudgets();
  }, [loadBudgets]);

  const goToCreate = useCallback(() => {
    setView("create");
  }, []);

  const openBudget = useCallback(async (budget: IBudget) => {
    if (!budget.uuid) return;

    const versions = await fetchBudgetVersionsV2(budget.uuid);
    const lastVersionNumber = versions.reduce((max, item) => Math.max(max, item.versionNumber ?? 0), 0);
    const latestVersion = versions.find((item) => item.versionNumber === lastVersionNumber);

    const isEditable =
      Boolean(latestVersion) &&
      !FINISHED_STATUSES.includes(latestVersion?.status ?? "") &&
      !/recusad|rejeitad/i.test(latestVersion?.status ?? "") &&
      isRecipientUser(budget.recipient ?? latestVersion?.recipient, persona);

    setCurrentBudgetId(budget.uuid);
    setCurrentVersion(lastVersionNumber || budget.lastVersionNumber);
    setView(isEditable ? (persona === "fleet-manager" ? "edit-manager" : "edit") : "details");
  }, [persona]);

  return {
    loading,
    budgets,
    profile,
    view,
    currentBudgetId,
    currentVersion,

    goToList,
    goToCreate,
    openBudget,
  };
};
